import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Plus, Edit, Trash2, MoreHorizontal, Phone, Mail, MapPin } from 'lucide-react';
import { ProtectedRoute } from '@/components/protected-route';
import { MainLayout } from '@/components/main-layout';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ApiCustomer } from '@/types';
import { CustomerForm } from '@/components/customers/customer-form';
import { useToast } from '@/hooks/use-toast';
import { useCustomers, useCreateCustomer, useUpdateCustomer, useDeleteCustomer } from '@/hooks/use-customers';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { CustomerFilters } from '@/components/customers/customer-filters';
import { PageHeader } from '@/components/page-header';
import { clampPage, resolvePageSize, resolveTotalItems, resolveTotalPages } from '@/lib/pagination';


export const Route = createFileRoute('/customers')({
  component: CustomersPage,
});

function CustomersPage() {
  const { toast } = useToast();
  const [filters, setFilters] = useState({
    search: '',
    startDate: undefined as Date | undefined,
    endDate: undefined as Date | undefined,
  });
  const [page, setPage] = useState(1);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<ApiCustomer | undefined>(undefined);

  const handleFiltersChange = (nextFilters: typeof filters) => {
    setPage(1);
    setFilters(nextFilters);
  };

  const { data: customersResponse, isLoading } = useCustomers({ 
    search: filters.search, 
    page,
  });
  const customers = customersResponse?.data || [];
  const pagination = customersResponse?.pagination;
  const pageSize = resolvePageSize(pagination);
  const totalItems = resolveTotalItems(pagination, customers.length);
  const totalPages = resolveTotalPages(pagination, customers.length);

  const createCustomer = useCreateCustomer();
  const updateCustomer = useUpdateCustomer();
  const deleteCustomer = useDeleteCustomer();

  const handleCreate = async (data: any) => {
    try {
      await createCustomer.mutateAsync({
        name: data.name,
        phone: data.phone,
        email: data.email,
        address: data.address,
      });
      setIsDialogOpen(false);
      toast({
        title: 'Customer Created',
        description: `${data.name} has been added successfully.`,
      });
    } catch (error: any) {
       const detail = error.response?.data?.detail || error.message || 'Failed to create customer';
       toast({ 
         title: 'Error', 
         description: detail, 
         variant: 'destructive' 
       });
    }
  };

  const handleUpdate = async (data: any) => {
    if (!editingCustomer) return;
    try {
      await updateCustomer.mutateAsync({ id: editingCustomer.id, data });
      setIsDialogOpen(false);
      setEditingCustomer(undefined);
      toast({
        title: 'Customer Updated',
        description: `${data.name} has been updated successfully.`,
      });
    } catch (error: any) {
       const detail = error.response?.data?.detail || error.message || 'Failed to update customer';
       toast({ 
         title: 'Error', 
         description: detail, 
         variant: 'destructive' 
       });
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this customer?')) {
      try {
        await deleteCustomer.mutateAsync(id);
        toast({
          title: 'Customer Deleted',
          description: 'Customer has been removed.',
          variant: 'destructive',
        });
      } catch {
         toast({ title: 'Error', description: 'Failed to delete customer', variant: 'destructive' });
      }
    }
  };

  const openEditDialog = (customer: ApiCustomer) => {
    setEditingCustomer(customer);
    setIsDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingCustomer(undefined);
    setIsDialogOpen(true);
  };

  return (
    <ProtectedRoute>
      <MainLayout>
        <div className="flex flex-col gap-6 min-w-0 max-w-full">
          <PageHeader
            title="Customers"
            actions={(
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <Button onClick={openCreateDialog} className="w-full sm:w-auto">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Customer
                </Button>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
                    </DialogTitle>
                    <DialogDescription>
                      {editingCustomer
                        ? 'Update customer details below.'
                        : 'Enter customer details to create a new profile.'}
                    </DialogDescription>
                  </DialogHeader>
                  <CustomerForm
                    initialData={editingCustomer}
                    onSubmit={editingCustomer ? handleUpdate : handleCreate}
                  />
                </DialogContent>
              </Dialog>
            )}
          />

          <CustomerFilters filters={filters} onFiltersChange={handleFiltersChange} />

          {isLoading ? (
            <div className="rounded-md border bg-card py-12 text-center">Loading...</div>
          ) : customers.length === 0 ? (
            <div className="rounded-md border bg-card py-12 text-center text-muted-foreground">No customers found.</div>
          ) : (
            <>
              <div className="space-y-3 md:hidden">
                {customers.map((customer) => (
                  <article key={customer.id} className="rounded-xl border bg-card p-4">
                    <Link
                      to="/customers/$id"
                      params={{ id: String(customer.id) }}
                      className="text-base font-semibold hover:text-primary hover:underline"
                    >
                      {customer.name}
                    </Link>

                    <div className="mt-2 flex flex-col gap-1.5 text-sm">
                      <div className="flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                        {customer.phone || '-'}
                      </div>
                      {customer.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="truncate">{customer.email}</span>
                        </div>
                      )}
                      {customer.address && (
                        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                          <MapPin className="h-3.5 w-3.5 shrink-0" />
                          <span className="line-clamp-2">{customer.address}</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <Button variant="outline" className="gap-2" onClick={() => openEditDialog(customer)}>
                        <Edit className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        className="gap-2 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(customer.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </article>
                ))}
              </div>

              <div className="hidden overflow-x-auto rounded-md border bg-white md:block dark:bg-card">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px] hidden md:table-cell">No</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Contact Info</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customers.map((customer, index) => (
                      <TableRow key={customer.id}>
                        <TableCell className="text-muted-foreground text-xs hidden md:table-cell">
                          {((pagination?.page || 1) - 1) * pageSize + index + 1}
                        </TableCell>
                        <TableCell className="font-medium">
                          <Link
                            to="/customers/$id"
                            params={{ id: String(customer.id) }}
                            className="hover:text-primary hover:underline"
                          >
                            {customer.name}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1 text-sm">
                            <div className="flex items-center gap-2">
                              <Phone className="h-3 w-3 text-muted-foreground" />
                              {customer.phone || '-'}
                            </div>
                            {customer.email && (
                              <div className="flex items-center gap-2">
                                <Mail className="h-3 w-3 text-muted-foreground" />
                                <span className="truncate max-w-[150px]">{customer.email}</span>
                              </div>
                            )}
                            {customer.address && (
                              <div className="flex items-center gap-2 text-muted-foreground/80 text-xs mt-1">
                                <MapPin className="h-3 w-3 shrink-0" />
                                <span className="truncate max-w-[150px]">{customer.address}</span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <span className="sr-only">Open menu</span>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuItem onClick={() => openEditDialog(customer)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => handleDelete(customer.id)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
          
          {/* Pagination */}
          {pagination && (
            <div className="mt-4">
               <PaginationControls 
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={(nextPage) => setPage(clampPage(nextPage, totalPages))}
                  totalItems={totalItems}
               />
            </div>
          )}
        </div>
      </MainLayout>
    </ProtectedRoute>
  );
}
